import { useState, useEffect, useCallback } from 'react';
import { fetchSalaries, SalaryRecord, SalaryResponse, SalaryParams } from '../api/salary';

interface UseSalariesResult {
  salaries: SalaryRecord[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  searchQuery: string;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setSearchQuery: (query: string) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  refetch: () => Promise<void>;
}

export const useSalaries = (pageSize: number = 10): UseSalariesResult => {
  const [salaries, setSalaries] = useState<SalaryRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchData = useCallback(async (page: number, search: string = searchQuery) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        PageNumber: page,
        PageSize: pageSize,
      };

      if (search && search.trim() !== "") {
        params.searchQuery = search;
      }

      const response: SalaryResponse = await fetchSalaries(params);

      if (response && response.response) {
        setSalaries(response.response);
        const totalRecords = response.totalCount || response.response.length;
        const totalPagesFromAPI = response.numberOfPages || Math.ceil(totalRecords / pageSize);
        
        setTotalCount(totalRecords);
        setTotalPages(totalPagesFromAPI);
      }
    } catch (err) {
      console.error("Failed to fetch salaries:", err);
      setError((err as Error).message || "Failed to load salaries");
    } finally {
      setLoading(false);
    }
  }, [pageSize, searchQuery]);

  useEffect(() => {
    fetchData(currentPage, searchQuery);
  }, [currentPage, fetchData]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    fetchData(1, query);
  }, [fetchData]);

  // Navigation handlers
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Refetch function
  const refetch = useCallback(async () => {
    await fetchData(currentPage, searchQuery);
  }, [currentPage, searchQuery, fetchData]);

  return {
    salaries,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    searchQuery,
    pageSize,
    setCurrentPage,
    setSearchQuery: handleSearchChange,
    goToNextPage,
    goToPreviousPage,
    refetch,
  };
};
